import { createPortal } from "react-dom";

export default function AddStaffModal({
  isOpen,
  formData,
  modalError,
  saving,
  loadingStaffId,
  onClose,
  onChange,
  onSubmit,
  onGeneratePin,
  opening,
}) {
  if (!isOpen) return null;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const minExpiryDate = tomorrow.toISOString().split("T")[0];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      {opening && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900"></div>
        </div>
      )}
      <div className="relative flex max-h-[95vh] w-full max-w-2xl flex-col rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Add Staff</h2>
            <p className="mt-1 text-sm text-slate-500">
              Create a new staff account
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={onChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="Enter first name"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={onChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="Enter last name"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Staff ID
                  </label>
                  <input
                    type="text"
                    name="staff_id"
                    value={formData.staff_id}
                    readOnly
                    className="w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm outline-none"
                    placeholder={
                      loadingStaffId ? "Generating..." : "Auto-generated"
                    }
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Hourly Rate (£)
                  </label>
                  <input
                    type="number"
                    name="hourly_rate"
                    value={formData.hourly_rate || ""}
                    onChange={onChange}
                    step="0.01"
                    min="0"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="Enter hourly pay"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    PIN
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="pin"
                      value={formData.pin}
                      onChange={onChange}
                      maxLength={4}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                      placeholder="Enter 4-digit PIN"
                    />
                    <button
                      type="button"
                      onClick={onGeneratePin}
                      disabled={saving}
                      className="whitespace-nowrap rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      Generate
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={onChange}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Staff Document
                </h3>
                <p className="mt-2 mb-4 text-sm text-slate-500">
                  Upload a staff document if available. Supported formats: JPG,
                  PNG, PDF.
                </p>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Document Type
                    </label>
                    <select
                      name="document_type"
                      value={formData.document_type || ""}
                      onChange={onChange}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                    >
                      <option value="">Select document</option>
                      <option value="passport">Passport</option>
                      <option value="id_card">ID Card</option>
                      <option value="drivers_license">Driver’s Licence</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      name="document_expiry"
                      value={formData.document_expiry || ""}
                      onChange={onChange}
                      min={minExpiryDate}
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Upload Document
                    </label>
                    <input
                      type="file"
                      name="document_file"
                      onChange={onChange}
                      className="w-full text-sm"
                      accept=".jpg,.jpeg,.png,.pdf"
                    />
                    {formData.document_file ? (
                      <p className="mt-2 text-xs text-slate-500">
                        Selected: {formData.document_file.name}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              {modalError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {modalError}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving || loadingStaffId}
              className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-70"
            >
              {saving ? "Saving..." : "Save Staff"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
